import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, Video, Award } from "lucide-react";
import { GoldButton, GhostButton } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="mask-line pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "2015", label: "Teaching since" },
  { value: "10,000+", label: "Students educated" },
  { value: "2024", label: "Officially registered" },
  { value: "100%", label: "Live online classes" },
];

export const Hero = ({ onQuiz, scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden spotlight" data-testid="hero-section">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[34rem] w-[54rem] -translate-x-1/2 rounded-full bg-gold/15 blur-[140px]"
      />
      <motion.div
        animate={{ y: [0, -26, 0], x: [0, 16, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute right-[6%] top-[24%] h-40 w-40 rounded-full border border-gold/30 bg-gold/5 blur-[2px]"
      />
      <motion.div
        animate={{ y: [0, 22, 0], x: [0, -18, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
        className="pointer-events-none absolute left-[5%] bottom-[16%] h-24 w-24 rounded-full border border-champagne/30 bg-champagne/5 blur-[2px]"
      />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="pointer-events-none absolute right-[14%] bottom-[24%] hidden lg:block font-display text-6xl text-gold/15 select-none"
      >
        ✦
      </motion.div>
      <motion.div
        style={{ y: textY }}
        className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 sm:px-8 lg:px-16 pt-28 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
          className="mb-8 inline-flex w-fit items-center gap-2.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 shadow-[0_0_40px_-10px_rgba(232,193,90,0.4)]"
          data-testid="hero-founder-badge"
        >
          <BadgeCheck size={15} className="text-gold shrink-0" />
          <span className="font-sans text-xs sm:text-sm text-champagne">
            Founded by <span className="font-medium text-neutral-100">Deepankshi Saxena</span>
            <span className="mx-2 text-gold/60">·</span>Teaching since 2015
            <span className="mx-2 text-gold/60">·</span>Registered 2024
          </span>
        </motion.div>

        <h1 className="font-display font-normal tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl text-neutral-100 max-w-4xl">
          <Line delay={0.45}>Master the skills that</Line>
          <Line delay={0.57}>
            build <em className="text-gold-gradient italic font-medium">brands</em> — and bankable
          </Line>
          <Line delay={0.69}>
            <em className="text-gold-gradient italic font-medium">careers.</em>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-7 max-w-2xl text-base sm:text-lg font-light text-neutral-400 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Live online classes on Google Meet. Certificates & diplomas that carry weight.
          One-on-one mentorship, career counselling and free freelancing mastery — for students,
          coaches, healers, brand owners and freelancers, in India and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <GoldButton testId="hero-explore-courses-button" onClick={() => scrollTo("#courses")}>
            Explore Courses & Batches <ArrowRight size={16} />
          </GoldButton>
          <GhostButton testId="hero-quiz-button" onClick={onQuiz}>
            Take the 5-Min Career Assessment
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          {[
            { icon: Video, label: "Live on Google Meet" },
            { icon: Award, label: "Certificate & Diploma" },
            { icon: BadgeCheck, label: "Free Freelancing Training" },
          ].map((f) => (
            <span key={f.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 font-sans text-sm text-neutral-300">
              <f.icon size={14} className="text-gold" /> {f.label}
            </span>
          ))}
        </motion.div>

        <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-gold/20 bg-gold/5 sm:grid-cols-4" data-testid="hero-stats">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.35 + i * 0.1, ease: EASE }}
              className="bg-void/80 px-6 py-6"
              data-testid={`hero-stat-${i}`}
            >
              <p className="font-display text-2xl sm:text-3xl text-champagne">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
